import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { Eyebrow, Reveal, GoldButton, EASE } from "@/components/site/motion";
import { IMAGES } from "@/data/content";
import { api, mediaUrl } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

const tabs = ["All", "News", "Blog", "Events", "Partnerships"];

export default function InsightsArchive() {
  useSEO(
    "Insights — Hapo Group",
    "Every story, announcement and perspective from across the Hapo Group."
  );
  const [active, setActive] = useState("All");
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api
      .insights()
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]));
  }, []);

  const items = (posts || []).filter((p) => active === "All" || p.category === active);

  return (
    <>
      <PageHero
        short
        eyebrow="Insights"
        lines={["Stories from across the group."]}
        description="News, perspectives, events and partnership announcements from all nine Hapo companies."
        image={IMAGES.office}
      />

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[120px]" aria-label="All insights" data-testid="insights-archive-page">
        <div className="flex flex-wrap gap-2 border-b border-ink/10" role="tablist" aria-label="Filter insights">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={active === t}
              data-testid={`archive-tab-${t.toLowerCase()}`}
              onClick={() => setActive(t)}
              className={`-mb-px border-b-2 px-4 py-4 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                active === t ? "border-gold text-ink" : "border-transparent text-smoke hover:text-ink"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {posts === null ? (
          <p className="mt-16 animate-pulse text-[11px] font-semibold uppercase tracking-[0.3em] text-smoke">
            Loading…
          </p>
        ) : items.length === 0 ? (
          <div className="mt-16" data-testid="archive-empty">
            <p className="text-lg font-semibold text-ink">No stories published in this section yet.</p>
            <p className="mt-3 text-sm text-smoke">Check back soon — or browse another section above.</p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="mt-12 grid gap-10 md:grid-cols-3"
              data-testid="archive-grid"
            >
              {items.map((a) => {
                const card = (
                  <>
                    {a.image && (
                      <div className="overflow-hidden">
                        <img
                          src={mediaUrl(a.image)}
                          alt=""
                          loading="lazy"
                          className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                      </div>
                    )}
                    <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.22em] text-gold">{a.tag}</p>
                    <h2 className="mt-2 text-lg font-bold leading-snug tracking-tight text-ink">{a.title}</h2>
                    <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-smoke">{a.date}</p>
                  </>
                );
                return a.link ? (
                  <a
                    key={a.id}
                    href={a.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                    data-testid="archive-card"
                  >
                    {card}
                    <span className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-smoke transition-colors group-hover:text-gold">
                      Read more <ArrowUpRight className="h-3.5 w-3.5 text-gold" />
                    </span>
                  </a>
                ) : (
                  <Link key={a.id} to={`/insights/${a.id}`} className="group block" data-testid="archive-card">
                    {card}
                    <span className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-smoke transition-colors group-hover:text-gold">
                      Read article <ArrowUpRight className="h-3.5 w-3.5 text-gold" />
                    </span>
                  </Link>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}
      </section>

      <section className="bg-ink px-6 py-24 text-center lg:px-12" aria-label="Contact the group">
        <Reveal>
          <Eyebrow className="justify-center">Hapo Group</Eyebrow>
          <h2 className="mx-auto mt-6 max-w-3xl text-3xl font-extrabold leading-tight tracking-tighter text-white sm:text-4xl">
            Want the next story to be about working with you?
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <GoldButton to="/contact" testId="archive-contact">
              Get in Touch
            </GoldButton>
          </div>
        </Reveal>
      </section>
    </>
  );
}
